import { useMemo } from 'react';

import type { FavoriteEntry } from './useLibrary';
import type { SourceId } from '@/sources/types';

/**
 * Title-based dedup heuristic across sources.
 *
 * Each source publishes a comic under its own title format — translated vs
 * original, with/without artist tags, romaji vs kana, etc. Exact-match
 * detection has too many false negatives; full-blown ML matching is
 * out of scope. Splitting the difference: a cheap normalization plus a
 * 2-gram word-shingle Jaccard threshold catches the obvious cases
 * (same comic, different sites) without flagging unrelated galleries that
 * happen to share a few stop words.
 *
 * Tuning the threshold:
 *  - Higher (e.g. 0.85): fewer false positives, more false negatives.
 *    Recommended if dedup is used to *merge* records.
 *  - Lower (e.g. 0.55): catches more cross-source matches; OK for the
 *    UI "did you mean these are the same?" affordance where a false hit
 *    is just visual noise.
 *
 * The default of 0.7 was chosen empirically against a handful of titles
 * across e-hentai / exhentai / hitomi. Adjust by setting
 * `useDedup(entries, { threshold: 0.X })`.
 */

export interface DedupGroup {
  /** Canonical normalized title for the group (the longest member's). */
  canonicalTitle: string;
  /** Members of the group, deduped per `(sourceId, comicId)`. */
  items: Array<{
    sourceId: SourceId;
    comicId: string;
    title: string;
    cover: string;
  }>;
}

export interface UseDedupOpts {
  /** Jaccard similarity threshold over 2-gram word shingles. Default 0.7. */
  threshold?: number;
  /** Cap on emitted groups. Default 50. */
  maxGroups?: number;
}

const STRIP_BRACKETS_RE = /^\[[^\]]*\]\s*/g;
const NON_ALPHANUM_RE = /[^\p{L}\p{N}\s]+/gu;
const COLLAPSE_WS_RE = /\s+/g;

/**
 * Normalize a title into a canonical comparison string. Pure; exported
 * for tests and for callers that want to expose the canonical form.
 *
 * Steps:
 *   1. Lower-case.
 *   2. Strip leading `[bracket]` artist/group tags (repeated).
 *   3. Strip all non-letter/digit characters except whitespace.
 *   4. Collapse whitespace and trim.
 */
export function normalizeTitle(input: string): string {
  let s = input.toLowerCase();
  // Repeatedly peel leading brackets (sources stack 2+ at times).
  let prev: string;
  do {
    prev = s;
    s = s.replace(STRIP_BRACKETS_RE, '');
  } while (s !== prev);
  s = s.replace(NON_ALPHANUM_RE, ' ').replace(COLLAPSE_WS_RE, ' ').trim();
  return s;
}

/**
 * Word-level 2-gram shingles. "the quick brown fox" → ["the quick",
 * "quick brown", "brown fox"]. Single-word inputs degrade to the word
 * itself so we still produce a meaningful comparison.
 */
export function shingles2(input: string): Set<string> {
  const words = input.split(' ').filter(Boolean);
  if (words.length === 0) return new Set();
  if (words.length === 1) return new Set([words[0]!]);
  const out = new Set<string>();
  for (let i = 0; i < words.length - 1; i++) {
    out.add(`${words[i]} ${words[i + 1]}`);
  }
  return out;
}

export function jaccard(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 && b.size === 0) return 1;
  if (a.size === 0 || b.size === 0) return 0;
  let intersection = 0;
  for (const v of a) {
    if (b.has(v)) intersection++;
  }
  const union = a.size + b.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

/**
 * Union-find via path compression. Used to coalesce transitively-similar
 * entries into a single group without an O(n³) closure pass.
 */
class DSU {
  private parent: number[];
  constructor(n: number) {
    this.parent = Array.from({ length: n }, (_, i) => i);
  }
  find(i: number): number {
    let root = i;
    while (this.parent[root] !== root) {
      root = this.parent[root]!;
    }
    let cur = i;
    while (this.parent[cur] !== root) {
      const next = this.parent[cur]!;
      this.parent[cur] = root;
      cur = next;
    }
    return root;
  }
  union(a: number, b: number): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent[ra] = rb;
  }
}

/**
 * Compute dedup groups for a flat list of favorites. Pure function, no
 * IndexedDB or React state. Exported so tests can exercise the algorithm
 * directly; the hook below is a thin `useMemo` wrapper.
 *
 * Groups with only one source are dropped — the entire point is
 * cross-source matches. A group with two members from the same source
 * (a user favorited the "same" comic twice on one source under different IDs)
 * is also dropped to keep the hint list focused.
 */
export function computeDedupGroups(
  entries: FavoriteEntry[],
  opts: UseDedupOpts = {},
): DedupGroup[] {
  const threshold = opts.threshold ?? 0.7;
  const maxGroups = opts.maxGroups ?? 50;
  if (entries.length === 0) return [];

  const norms = entries.map((e) => normalizeTitle(e.title));
  const sets = norms.map((n) => shingles2(n));
  const dsu = new DSU(entries.length);

  // Exact-match groups first — cheap.
  const byNorm = new Map<string, number[]>();
  for (let i = 0; i < entries.length; i++) {
    if (!norms[i]) continue;
    const bucket = byNorm.get(norms[i]!) ?? [];
    bucket.push(i);
    byNorm.set(norms[i]!, bucket);
  }
  for (const indices of byNorm.values()) {
    for (let k = 1; k < indices.length; k++) {
      dsu.union(indices[0]!, indices[k]!);
    }
  }

  // Pairwise similarity pass for non-exact matches. O(n²) but n is
  // typically <= a few hundred favorites — fine on the main thread.
  for (let i = 0; i < entries.length; i++) {
    if (!norms[i]) continue;
    for (let j = i + 1; j < entries.length; j++) {
      if (!norms[j]) continue;
      if (norms[i] === norms[j]) continue; // already unioned above
      // Skip the work if shingles are wildly mismatched in size — Jaccard
      // can never exceed min/max of cardinalities.
      const sa = sets[i]!;
      const sb = sets[j]!;
      const lo = Math.min(sa.size, sb.size);
      const hi = Math.max(sa.size, sb.size);
      if (hi === 0 || lo / hi < threshold) continue;
      if (jaccard(sa, sb) >= threshold) dsu.union(i, j);
    }
  }

  // Bucket entries by their union-find root.
  const buckets = new Map<number, number[]>();
  for (let i = 0; i < entries.length; i++) {
    if (!norms[i]) continue; // skip empty-normalized titles
    const r = dsu.find(i);
    const arr = buckets.get(r) ?? [];
    arr.push(i);
    buckets.set(r, arr);
  }

  const groups: DedupGroup[] = [];
  for (const indices of buckets.values()) {
    if (indices.length < 2) continue;
    const sourceSet = new Set<SourceId>();
    for (const i of indices) sourceSet.add(entries[i]!.sourceId);
    if (sourceSet.size < 2) continue; // not cross-source

    // Dedup `(sourceId, comicId)` and pick the longest title as canonical.
    const seenItem = new Set<string>();
    const items: DedupGroup['items'] = [];
    let canonical = '';
    for (const i of indices) {
      const e = entries[i]!;
      const key = `${e.sourceId}|${e.comicId}`;
      if (seenItem.has(key)) continue;
      seenItem.add(key);
      items.push({
        sourceId: e.sourceId,
        comicId: e.comicId,
        title: e.title,
        cover: e.cover,
      });
      if (e.title.length > canonical.length) canonical = e.title;
    }
    if (items.length < 2) continue;
    groups.push({ canonicalTitle: canonical, items });
  }

  groups.sort((a, b) => b.items.length - a.items.length);
  return groups.slice(0, maxGroups);
}

/**
 * `useMemo`-wrapped variant of {@link computeDedupGroups}. Recomputes only
 * when `entries`, the threshold, or `maxGroups` change.
 */
export function useDedup(
  entries: FavoriteEntry[],
  opts: UseDedupOpts = {},
): DedupGroup[] {
  const threshold = opts.threshold ?? 0.7;
  const maxGroups = opts.maxGroups ?? 50;
  return useMemo(
    () => computeDedupGroups(entries, { threshold, maxGroups }),
    [entries, threshold, maxGroups],
  );
}
